import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { vi as viDateFns } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MascotConfirmDialog } from "@/components/common/MascotConfirmDialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useNotifications, type Notification } from "@/hooks/use-notifications";
import { useProductUpdates, type Update, type UpdateCategory } from "@lyder-talk/core/hooks/use-product-updates";
import { useSeenUpdates } from "@lyder-talk/core/hooks/use-seen-updates";
import { Bell, CheckCheck, ExternalLink, Users, Share2, UserPlus, MessageSquare, Trophy, Megaphone, Trash2, Sparkles, Zap, Wrench, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { commentPreviewText } from "@/lib/sanitize-html";
import { getNotificationLink } from "@/lib/notification-links";
import { getRootUrl } from "@lyder-talk/core";

// Custom event name for notifications update
const NOTIFICATIONS_UPDATED_EVENT = "notifications-updated";

interface NotificationsDropdownProps {
    children: React.ReactNode;
    userId: string | null;
}

const getNotificationIcon = (type: string) => {
    switch (type) {
        case "session_shared":
            return Share2;
        case "session_deleted":
            return Share2;
        case "member_joined":
            return UserPlus;
        case "member_left":
            return UserPlus;
        case "invitation":
            return Users;
        case "mention":
            return MessageSquare;
        case "session_comment":
        case "session_comment_reply":
            return MessageSquare;
        case "challenge":
            return Trophy;
        case "announcement":
            return Megaphone;
        default:
            return Bell;
    }
};

const getNotificationColor = (type: string) => {
    switch (type) {
        case "session_shared":
            return "text-blue-500";
        case "session_deleted":
            return "text-gray-500";
        case "member_joined":
            return "text-green-500";
        case "member_left":
            return "text-orange-500";
        case "invitation":
            return "text-blue-500";
        case "mention":
            return "text-orange-500";
        case "session_comment":
        case "session_comment_reply":
            return "text-cyan-500";
        case "challenge":
            return "text-yellow-500";
        case "announcement":
            return "text-red-500";
        default:
            return "text-muted-foreground";
    }
};

const categoryConfig: Record<
    UpdateCategory,
    { label: string; className: string; icon: typeof Sparkles }
> = {
    feature: {
        label: "New Feature",
        className: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
        icon: Sparkles,
    },
    improvement: {
        label: "Improvement",
        className: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
        icon: Zap,
    },
    fix: {
        label: "Bug Fix",
        className: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
        icon: Wrench,
    },
    announcement: {
        label: "Announcement",
        className: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
        icon: Megaphone,
    },
};

const getCategoryColor = (category: UpdateCategory) => {
    switch (category) {
        case "feature":
            return "text-blue-500";
        case "improvement":
            return "text-blue-500";
        case "fix":
            return "text-amber-500";
        case "announcement":
            return "text-blue-500";
        default:
            return "text-muted-foreground";
    }
};

// Notification messages are assembled server-side and, for comment notifications,
// carry the first 100 characters of another learner's stored comment HTML. Reduce
// them through an INERT parse (DOMParser, no browsing context): a detached element
// built with document.createElement still belongs to the active document, so its
// images load and their onerror handlers run even though it is never inserted.
// That detached-div innerHTML trick was a stored-XSS primitive here.
const formatNotificationMessage = (notification: Notification): string => {
    if (notification.notification_type === "session_comment") {
        const match = notification.message.match(/^(.+?) commented on "(.+?)":/);
        if (match) {
            const preview = commentPreviewText(notification.message.slice(match[0].length));
            const truncated = preview.length > 80 ? preview.slice(0, 80) + "..." : preview;
            return `${match[1]} commented on ${match[2]}${truncated ? `: ${truncated}` : ""}`;
        }
    }
    if (notification.notification_type === "session_comment_reply") {
        const match = notification.message.match(/^(.+?) replied to your comment on "(.+?)":/);
        if (match) {
            const preview = commentPreviewText(notification.message.slice(match[0].length));
            const truncated = preview.length > 80 ? preview.slice(0, 80) + "..." : preview;
            return `${match[1]} replied on ${match[2]}${truncated ? `: ${truncated}` : ""}`;
        }
    }
    return commentPreviewText(notification.message);
};

type NotificationTab = "all" | "activity" | "social" | "invites" | "updates";

const NOTIFICATION_TABS: { id: NotificationTab; label: string; types: string[] }[] = [
    { id: "all", label: "All", types: [] },
    { id: "activity", label: "Activity", types: ["session_shared", "session_deleted", "session_comment", "session_comment_reply"] },
    { id: "social", label: "Social", types: ["member_joined", "member_left", "mention"] },
    { id: "invites", label: "Invites", types: ["invitation"] },
    { id: "updates", label: "Updates", types: ["announcement", "challenge"] },
];

const filterNotificationsByTab = (notifications: Notification[], tab: NotificationTab): Notification[] => {
    if (tab === "all") return notifications;
    const tabConfig = NOTIFICATION_TABS.find(t => t.id === tab);
    if (!tabConfig) return notifications;
    return notifications.filter(n => tabConfig.types.includes(n.notification_type));
};

export const NotificationsDropdown = ({ children, userId }: NotificationsDropdownProps) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<NotificationTab>("all");
    const [notificationToDelete, setNotificationToDelete] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const { notifications, loading, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications(userId, { enabled: open });
    const { t, i18n } = useTranslation();
    const { updates } = useProductUpdates({ dateFilter: "all-time" });
    const { markAsSeen, hasSeen } = useSeenUpdates();

    const handleNotificationClick = async (notification: Notification) => {
        if (!notification.is_read) {
            await markAsRead(notification.id);
        }
        const link = await getNotificationLink(notification);
        if (link) {
            navigate(link);
        }
    };

    const handleDeleteClick = (e: React.MouseEvent, notificationId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setNotificationToDelete(notificationId);
    };

    const handleConfirmDelete = async () => {
        if (notificationToDelete) {
            await deleteNotification(notificationToDelete);
            setNotificationToDelete(null);
        }
    };

    const handleProductUpdateClick = (update: Update) => {
        markAsSeen(update.id);
        window.location.href = getRootUrl(`/updates/${update.id}`);
    };

    const handleMarkAllAsRead = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        await markAllAsRead();
        window.dispatchEvent(new Event(NOTIFICATIONS_UPDATED_EVENT));
    };

    const filteredNotifications = filterNotificationsByTab(notifications, activeTab);
    const unreadNotifications = filteredNotifications.filter(n => !n.is_read);
    const readNotifications = filteredNotifications.filter(n => n.is_read);

    const showProductUpdates = activeTab === "updates" || activeTab === "all";
    const recentUpdates = updates.slice(0, 10);

    return (
        <>
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                    {children}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[320px] p-0">
                    <div className="flex items-center justify-between px-3 py-2 border-b">
                        <DropdownMenuLabel className="flex items-center gap-1.5 p-0 text-sm">
                            <Bell className="h-4 w-4" />
                            {t("pages.notifications.header", "Notifications")}
                            {unreadCount > 0 && (
                            <Badge variant="default" className="ml-1 h-4 min-w-4 px-1 text-[11px]">
                                {unreadCount}
                            </Badge>
                        )}
                    </DropdownMenuLabel>
                    {unreadCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleMarkAllAsRead}
                            className="h-7 text-[11px] hover:bg-transparent px-0 text-primary"
                        >
                            <CheckCheck className="h-3 w-3 mr-1" />
                            {t("pages.notifications.markAllRead", "Mark all read")}
                        </Button>
                    )}
                </div>

                {/* Notification Type Tabs */}
                <div className="flex items-center gap-1 px-2 py-1.5 border-b overflow-x-auto">
                    {NOTIFICATION_TABS.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setActiveTab(tab.id);
                            }}
                            className={cn(
                                "px-2.5 py-0.5 text-[11px] font-medium rounded-full whitespace-nowrap transition-colors",
                                activeTab === tab.id
                                    ? "bg-primary-strong text-primary-foreground"
                                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                            )}
                        >
                            {t(`pages.notifications.tabs.${tab.id}`, tab.label)}
                        </button>
                    ))}
                </div>

                <ScrollArea className="h-[300px]">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="text-sm text-muted-foreground">{t("pages.notifications.loading", "Loading notifications...")}</div>
                        </div>
                    ) : filteredNotifications.length === 0 && recentUpdates.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                            <Bell className="h-8 w-8 text-muted-foreground/50 mb-3" />
                            <p className="text-sm font-medium text-foreground mb-1">
                                {activeTab === "all"
                                ? t("pages.notifications.noNotifications", "No notifications")
                                : t("pages.notifications.noTabNotifications", { tab: t(`pages.notifications.tabs.${activeTab}`) })}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {activeTab === "all"
                                    ? t("pages.notifications.emptyAll", "You'll see notifications here when something happens in your study groups")
                                    : activeTab === "updates"
                                    ? t("pages.notifications.emptyUpdates", "Check back soon for product updates and announcements")
                                    : t("pages.notifications.emptyOther", "Try checking other tabs for more notifications")
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="py-2">
                            {/* Unread notifications */}
                            {unreadNotifications.length > 0 && (
                                <>
                                    {unreadNotifications.map((notification) => {
                                        const Icon = getNotificationIcon(notification.notification_type);
                                        const iconColor = getNotificationColor(notification.notification_type);

                                        return (
                                            <DropdownMenuItem
                                                key={notification.id}
                                                className={cn(
                                                    "flex flex-col items-start gap-1 px-3 py-2 cursor-pointer focus:bg-muted/50 transition-colors border-l-2 border-l-primary mb-0.5 mx-1 rounded-sm",
                                                    "bg-primary/5"
                                                )}
                                                onClick={() => handleNotificationClick(notification)}
                                            >
                                                <div className="flex items-start gap-2 w-full">
                                                    <div className={cn("mt-0.5 shrink-0", iconColor)}>
                                                        <Icon className="h-4 w-4" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[13px] leading-snug text-foreground line-clamp-2">
                                                            {formatNotificationMessage(notification)}
                                                        </p>
                                                        <div className="flex items-center justify-between mt-1">
                                                            <span className="text-[11px] text-muted-foreground">
                                                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: i18n.language === "vi" ? viDateFns : undefined })}
                                                            </span>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
                                                                onClick={(e) => handleDeleteClick(e, notification.id)}
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                        );
                                    })}
                                    {readNotifications.length > 0 && (
                                        <DropdownMenuSeparator className="my-2" />
                                    )}
                                </>
                            )}

                            {/* Read notifications */}
                            {readNotifications.map((notification) => {
                                const Icon = getNotificationIcon(notification.notification_type);
                                const iconColor = getNotificationColor(notification.notification_type);

                                return (
                                    <DropdownMenuItem
                                        key={notification.id}
                                        className="flex flex-col items-start gap-1 px-3 py-2 cursor-pointer focus:bg-muted/50 transition-colors mx-1 rounded-sm"
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="flex items-start gap-2 w-full">
                                            <div className={cn("mt-0.5 shrink-0", iconColor, "opacity-60")}>
                                                <Icon className="h-4 w-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[13px] leading-snug text-foreground line-clamp-2 opacity-60">
                                                    {formatNotificationMessage(notification)}
                                                </p>
                                                <div className="flex items-center justify-between mt-1">
                                                    <span className="text-[11px] text-muted-foreground">
                                                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: i18n.language === "vi" ? viDateFns : undefined })}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
                                                        onClick={(e) => handleDeleteClick(e, notification.id)}
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </DropdownMenuItem>
                                );
                            })}

                            {/* Product Updates in Updates tab */}
                            {showProductUpdates && recentUpdates.length > 0 && (
                                <>
                                    {(activeTab === "updates") && (unreadNotifications.length > 0 || readNotifications.length > 0) && (
                                        <DropdownMenuSeparator className="my-2" />
                                    )}
                                    {recentUpdates.map((update) => {
                                        const config = categoryConfig[update.category];
                                        const Icon = config.icon;
                                        const iconColor = getCategoryColor(update.category);
                                        const unseen = !hasSeen(update.id);

                                        return (
                                            <DropdownMenuItem
                                                key={update.id}
                                                className={cn(
                                                    "flex flex-col items-start gap-1 px-3 py-2 cursor-pointer focus:bg-muted/50 transition-colors mx-1 rounded-sm",
                                                    unseen && "border-l-2 border-l-primary bg-primary/5"
                                                )}
                                                onClick={() => handleProductUpdateClick(update)}
                                            >
                                                <div className="flex items-start gap-2 w-full">
                                                    <div className={cn("mt-0.5 shrink-0", iconColor, !unseen && "opacity-60")}>
                                                        <Icon className="h-4 w-4" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start justify-between gap-2">
                                                            <p className={cn("text-[13px] font-medium text-foreground line-clamp-1", !unseen && "opacity-60")}>
                                                                {update.title}
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <Badge
                                                                variant="outline"
                                                                className={cn("text-[11px] h-4 px-1.5", config.className)}
                                                            >
                                                                {t(`pages.notifications.categories.${update.category}`, config.label)}
                                                            </Badge>
                                                        </div>
                                                        <div className="flex items-center justify-between mt-1">
                                                            <span className="text-[11px] text-muted-foreground">
                                                                {formatDistanceToNow(new Date(update.date), { addSuffix: true, locale: i18n.language === "vi" ? viDateFns : undefined })}
                                                            </span>
                                                            <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    )}
                </ScrollArea>
            </DropdownMenuContent>
        </DropdownMenu>

        <MascotConfirmDialog
            open={!!notificationToDelete}
            onOpenChange={(open) => {
                if (!open) setNotificationToDelete(null);
            }}
            onConfirm={handleConfirmDelete}
            title={t("pages.notifications.deleteTitle", "Delete Notification")}
            description={t("pages.notifications.deleteDescription", "Are you sure you want to delete this notification? This action cannot be undone.")}
            confirmLabel={t("pages.notifications.deleteConfirm", "Delete")}
            cancelLabel={t("common.actions.cancel", "Cancel")}
            destructive
        />
        </>
    );
};
